/**
 * CYF default branding configuration.
 * Used as the baseline theme for non-whitelabel instances and as
 * fallback / reset values in the admin Theming page.
 */

export const CYF_COLORS = {
  charcoal: '#1C2B26',
  moss: '#2D4A3E',
  forest: '#3D6B57',
  sage: '#5C8C6E',
  fern: '#8BB89A',
  mist: '#C8DDD1',
  cream: '#F5F0E8',
  white: '#FDFCF9',
  amber: '#C4883A',
  amberLight: '#E8B96A',
  amberPale: '#F5E6CC',
};

export const cyfDefaultBrandingColors = {
  mainLogoBackground: CYF_COLORS.charcoal,
  primaryColor: CYF_COLORS.sage,
  secondaryColor: CYF_COLORS.amber,
  accentColor: CYF_COLORS.moss,
  primaryButton: CYF_COLORS.sage,
  btnTextColor: '#FFFFFF',
  buttonHover: CYF_COLORS.forest,
  bgColor: CYF_COLORS.cream,
  textColor: CYF_COLORS.charcoal,
  cardBackground: CYF_COLORS.white,
  headerBackground: CYF_COLORS.white,
  primaryHeaderText: CYF_COLORS.charcoal,
  primaryHeaderBackground: CYF_COLORS.white,
  sidebarBackground: CYF_COLORS.charcoal,
  sidebarLinkColor: 'rgba(245, 240, 232, 0.6)',
  sidebarLinkHover: CYF_COLORS.cream,
  sidebarLinkFocus: CYF_COLORS.fern,
  sidebarLinkBackgroundHover: CYF_COLORS.moss,
  sidebarLinkBackgroundFocus: 'rgba(139, 184, 154, 0.08)',
  informationBtnBackground: CYF_COLORS.sage,
  informationBtnBackgroundHover: CYF_COLORS.moss,
  menuToggleBackground: CYF_COLORS.sage,
  menuToggleBackgroundHover: CYF_COLORS.forest,
  hamburgerMenuIcon: CYF_COLORS.cream,
  hamburgerMenuIconHover: CYF_COLORS.white,
  navbarUserDropdownBg: CYF_COLORS.cream,
  tooltipBackground: CYF_COLORS.charcoal,
  tooltipText: CYF_COLORS.cream,
  breadCrumbsBackground: CYF_COLORS.sage,
  breadCrumbsText: '#FFFFFF',
  categoryBackground: CYF_COLORS.moss,
  categoryText: CYF_COLORS.cream,
  primaryLinkText: CYF_COLORS.sage,
  signInFormBg: CYF_COLORS.white,
  signInLogoSideBg: CYF_COLORS.charcoal,
  cancelBtn: CYF_COLORS.moss,
  dashboardSecondIcon: CYF_COLORS.amber,
  dashboardIconBtnTxtColor: '#FFFFFF',
  successColor: '#4A9E6B',
  successBgColor: '#EBF7F0',
  warningColor: CYF_COLORS.amber,
  warningBgColor: '#FBF0DC',
  dangerColor: '#C0392B',
  dangerBgColor: '#FDECEA',
  infoColor: '#2E7BB4',
  infoBgColor: '#E8F3FB',
  mutedColor: CYF_COLORS.mist,
  mutedForeground: '#4A6359',
  borderColor: 'rgba(45, 74, 62, 0.12)',
  inputBorderColor: 'rgba(45, 74, 62, 0.22)',
  popoverBackground: CYF_COLORS.white,
  popoverForeground: CYF_COLORS.charcoal,
  sidebarAccent: 'rgba(139, 184, 154, 0.08)',
  sidebarAccentForeground: CYF_COLORS.fern,
  sidebarBorder: 'rgba(245, 240, 232, 0.15)',
  chart1: CYF_COLORS.sage,
  chart2: CYF_COLORS.amber,
  chart3: CYF_COLORS.moss,
  chart4: CYF_COLORS.fern,
  chart5: CYF_COLORS.forest,
  palette: [CYF_COLORS.sage, CYF_COLORS.amber, CYF_COLORS.moss, CYF_COLORS.fern, CYF_COLORS.forest],
  borderRadius: '8px',
};

export const cyfDefaultFonts = [
  {
    font: {
      body: "'DM Sans', system-ui, sans-serif",
      headers: "'DM Serif Display', Georgia, serif",
      mono: "'Space Mono', 'Courier New', monospace",
    },
    googleFontsUrl: null,
    customFontCss: null,
    size: ['2.3em', '2em', '1.1em'],
    weight: [300, 400, 500, 600, 700, 900],
    style: 'italic',
  },
];

export const wlDefaultFonts = [
  {
    font: {
      body: "'Open Sans', sans-serif",
      headers: "'Open Sans', sans-serif",
      mono: "'Courier New', monospace",
    },
    googleFontsUrl: null,
    customFontCss: null,
    size: ['2.3em', '2em', '1.1em'],
    weight: [300, 400, 500, 600, 700, 900],
    style: 'italic',
  },
];

export const cyfDefaultBranding = {
  colors: cyfDefaultBrandingColors,
  fonts: cyfDefaultFonts,
  name: 'Compare Your Footprint',
};

export const POPULAR_GOOGLE_FONTS = [
  { label: 'Poppins', value: 'Poppins', url: 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap' },
  { label: 'Inter', value: 'Inter', url: 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap' },
  { label: 'Lato', value: 'Lato', url: 'https://fonts.googleapis.com/css2?family=Lato:wght@300;400;700;900&display=swap' },
  { label: 'Montserrat', value: 'Montserrat', url: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap' },
  { label: 'Nunito', value: 'Nunito', url: 'https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;600;700&display=swap' },
  { label: 'Raleway', value: 'Raleway', url: 'https://fonts.googleapis.com/css2?family=Raleway:wght@300;400;500;600;700&display=swap' },
  { label: 'Roboto', value: 'Roboto', url: 'https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap' },
  { label: 'Source Sans 3', value: 'Source Sans 3', url: 'https://fonts.googleapis.com/css2?family=Source+Sans+3:wght@300;400;600;700&display=swap' },
  { label: 'Work Sans', value: 'Work Sans', url: 'https://fonts.googleapis.com/css2?family=Work+Sans:wght@300;400;500;600;700&display=swap' },
  { label: 'Outfit', value: 'Outfit', url: 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap' },
];

const hexToRgb = (hex) => {
  if (!hex || typeof hex !== 'string') return null;
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return null;
  return {
    r: parseInt(clean.slice(0, 2), 16),
    g: parseInt(clean.slice(2, 4), 16),
    b: parseInt(clean.slice(4, 6), 16),
  };
};

const rgbToHex = (r, g, b) =>
  '#' + [r, g, b].map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, '0')).join('');

const lighten = (hex, amount = 0.2) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return rgbToHex(
    rgb.r + (255 - rgb.r) * amount,
    rgb.g + (255 - rgb.g) * amount,
    rgb.b + (255 - rgb.b) * amount
  );
};

const darken = (hex, amount = 0.2) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return rgbToHex(rgb.r * (1 - amount), rgb.g * (1 - amount), rgb.b * (1 - amount));
};

const withOpacity = (hex, opacity) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
};

const desaturate = (hex, amount = 0.3) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const gray = rgb.r * 0.299 + rgb.g * 0.587 + rgb.b * 0.114;
  return rgbToHex(
    rgb.r + (gray - rgb.r) * amount,
    rgb.g + (gray - rgb.g) * amount,
    rgb.b + (gray - rgb.b) * amount
  );
};

/**
 * Takes a partial brandingColors object and fills in any missing extended
 * token keys by auto-deriving them from the core colors.
 */
export const deriveThemeTokens = (colors = {}) => {
  const primary = colors.primaryColor || colors.mainLogoBackground || '#5C8C6E';
  const secondary = colors.secondaryColor || '#C4883A';
  const bg = colors.bgColor || '#F5F0E8';
  const text = colors.textColor || '#1C2B26';
  const card = colors.cardBackground || '#FDFCF9';
  const sidebar = colors.sidebarBackground || '#1C2B26';
  const button = colors.primaryButton || primary;

  return {
    ...colors,
    successColor: colors.successColor || '#4A9E6B',
    successBgColor: colors.successBgColor || lighten('#4A9E6B', 0.85),
    warningColor: colors.warningColor || '#C4883A',
    warningBgColor: colors.warningBgColor || lighten('#C4883A', 0.85),
    dangerColor: colors.dangerColor || '#C0392B',
    dangerBgColor: colors.dangerBgColor || lighten('#C0392B', 0.85),
    infoColor: colors.infoColor || '#2E7BB4',
    infoBgColor: colors.infoBgColor || lighten('#2E7BB4', 0.85),
    mutedColor: colors.mutedColor || lighten(bg, 0.3),
    mutedForeground: colors.mutedForeground || desaturate(text, 0.5),
    borderColor: colors.borderColor || withOpacity(primary, 0.12),
    inputBorderColor: colors.inputBorderColor || withOpacity(primary, 0.22),
    popoverBackground: colors.popoverBackground || card,
    popoverForeground: colors.popoverForeground || text,
    sidebarAccent: colors.sidebarAccent || withOpacity(button, 0.08),
    sidebarAccentForeground: colors.sidebarAccentForeground || colors.sidebarLinkFocus || lighten(primary, 0.3),
    sidebarBorder: colors.sidebarBorder || withOpacity(bg, 0.15),
    chart1: colors.chart1 || primary,
    chart2: colors.chart2 || secondary,
    chart3: colors.chart3 || darken(primary, 0.3),
    chart4: colors.chart4 || lighten(primary, 0.3),
    chart5: colors.chart5 || darken(secondary, 0.2),
    palette: colors.palette || [primary, secondary, darken(primary, 0.3), lighten(primary, 0.3), darken(secondary, 0.2)],
    borderRadius: colors.borderRadius || '8px',
    buttonHover: colors.buttonHover || darken(button, 0.15),
    tooltipBackground: colors.tooltipBackground || sidebar,
    tooltipText: colors.tooltipText || bg,
    breadCrumbsBackground: colors.breadCrumbsBackground || primary,
    breadCrumbsText: colors.breadCrumbsText || '#FFFFFF',
    categoryBackground: colors.categoryBackground || darken(primary, 0.3),
    categoryText: colors.categoryText || bg,
    primaryLinkText: colors.primaryLinkText || primary,
    cancelBtn: colors.cancelBtn || darken(primary, 0.3),
    dashboardSecondIcon: colors.dashboardSecondIcon || secondary,
    dashboardIconBtnTxtColor: colors.dashboardIconBtnTxtColor || '#FFFFFF',
    primaryHeaderText: colors.primaryHeaderText || text,
    primaryHeaderBackground: colors.primaryHeaderBackground || card,
    signInFormBg: colors.signInFormBg || card,
    signInLogoSideBg: colors.signInLogoSideBg || sidebar,
  };
};
